import type { AtcRow, VersionDuration } from '@capsio/contracts';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { api } from '../api-client.js';
import { AtcBaselineForm } from './AtcBaselineForm.js';

/**
 * 「基线对照」块（C-191 工作三）：默认是**对照**，点「✎ 编辑」才是表单。
 *
 * ## 为什么默认态不是表单
 *
 * 上一版这里常驻 AtcBaselineForm——四个输入框加复选框加文本域横在页面上，
 * 看着像「横着一个表格填空」。而这一块的日常问题是**读**：
 * 改造成效 = 分母（改造前人工）对各版实测均耗。分母单独摆着读不出成效，
 * 分子（逐版实测）也要在——所以默认态是几行数的对照，不是表格框。
 *
 * ## 表单一行未改
 *
 * 编辑态渲染的还是 AtcBaselineForm 原样——基线五项的字段与校验全在它里面，
 * 包括「已有基线改动必填修订理由」的拦截（基线是分母，悄悄改分母等于改结论）。
 * 这里只多一道进出：进去点「✎ 编辑」，出来靠保存或「取消」。
 *
 * ## 数从哪来
 *
 * 两路：基线五项从 `/api/org/atcs` 的行上取（取法与 AtcBaselineForm 相同），
 * 逐版实测均耗从 `/api/exec/atcs/:code/version-durations` 取（C-191 新端点，
 * 源视图 v_atc_version_avg_duration）。**降幅（vsBaseline）由库算，界面不除**——
 * 与「baselineDailyMinutes 由库算」是同一条 SSOT 纪律。
 */

/** 频次周期怎么念。只有这一处——两处各写一遍必然漂移。 */
const FREQUENCY_UNIT_LABEL = { day: '次/天', week: '次/周', month: '次/月' } as const;

export function BaselineComparison({
  baselineDurationMin,
  baselineFrequency,
  baselineFrequencyUnit,
  baselineFrequencyEstimated,
  baselineDailyMinutes,
  durations,
  currentVersionNo,
}: {
  /** 基线单次耗时（改造前，人做一次要多久）。null = 还没录基线（零态）。 */
  baselineDurationMin: number | null;
  baselineFrequency: number | null;
  baselineFrequencyUnit: 'day' | 'week' | 'month' | null;
  /** true = 频次是调研估计值。估的和实测的长得一样，不标就分不出来。 */
  baselineFrequencyEstimated: boolean;
  /** 基线日总耗时。**库算的**（v_atc_baseline_daily_minutes），界面不乘。 */
  baselineDailyMinutes: number | null;
  /** 逐版实测均耗，按 versionNo 升序。空数组 = 还没有成功的执行记录（零态）。 */
  durations: VersionDuration[];
  /** 当前版本号。null = 还没读到，写「当前版本」不带号。 */
  currentVersionNo: number | null;
}): ReactNode {
  const hasBaseline = baselineDurationMin !== null;

  /*
   * 拼接先行：相邻的文本与表达式经 renderToStaticMarkup 会插 <!-- --> 注释节点，
   * 断言与复制粘贴都会被它截断——所以每段先拼成完整字符串再交给 JSX。
   */
  return (
    <div>
      {hasBaseline ? (
        <p>
          <span className="sub">基线（V0 · 人工）</span>{' '}
          <span className="mono">{`${baselineDurationMin} min/次`}</span>
          {/*
            频次与日总只在「还没有执行记录」时摊开：那时它们是仅有的信息。
            一旦有了逐版实测，基线行只留 per-time——分母一个数就够，别把两行读成一行。
          */}
          {durations.length === 0 &&
            baselineFrequency !== null &&
            baselineFrequencyUnit !== null && (
              <span className="mono">{` × ${baselineFrequency} ${FREQUENCY_UNIT_LABEL[baselineFrequencyUnit]}`}</span>
            )}
          {durations.length === 0 && baselineDailyMinutes !== null && (
            <span className="mono">{` = ${baselineDailyMinutes} min/天`}</span>
          )}
        </p>
      ) : (
        <p className="zero">尚未记录改造前基线</p>
      )}

      {hasBaseline && baselineFrequencyEstimated && <p className="sub-hint">频次为调研估计值</p>}

      {durations.length === 0
        ? hasBaseline && (
            <p>
              <span className="sub">
                {`当前（${currentVersionNo === null ? '当前版本' : `v${currentVersionNo}`}）`}
              </span>{' '}
              {/* 零态照实说「没有」——视图就在库里，这一格不是无源。 */}
              <span className="zero">尚无执行记录</span>
            </p>
          )
        : durations.map((d) => (
            <p key={d.versionNo}>
              <span className="mono">{`v${d.versionNo}`}</span>{' '}
              <span className="mono">{`${d.avgMinutes} min/次`}</span>
              {/*
                箭头与百分比由 vsBaseline 出：>= 0 显 ↓，< 0 显 ↑，null 不显。
                符号由箭头承担，百分比取绝对值——「↑ -25%」是把方向说了两遍。
                null = 没录基线，没有分母就谈不上「省下多少」——不是 0%，也不是 0 箭头。
              */}
              {d.vsBaseline !== null && (
                <span className="mono">
                  {'　'}
                  {`${d.vsBaseline >= 0 ? '↓' : '↑'} ${Math.round(Math.abs(d.vsBaseline) * 100)}%`}
                </span>
              )}
            </p>
          ))}

      {baselineDailyMinutes !== null && (
        <p className="sub-hint">
          {`按当前值算，这张卡一天占 ${baselineDailyMinutes} 分钟。这个数由库算，不在界面上乘。`}
        </p>
      )}
    </div>
  );
}

export function AtcBaselineBlock({
  code,
  canManage,
  currentVersionNo,
  onChanged,
}: {
  /** 卡编码。基线行与逐版均耗都按它取。 */
  code: string;
  canManage: boolean;
  /** 当前版本号，传给展示态的「当前（vN）」。 */
  currentVersionNo: number | null;
  /** 基线改了，外层重新拉树/卡片——日总耗时变了，别的地方也写着它。 */
  onChanged: () => void;
}): ReactNode {
  const [row, setRow] = useState<AtcRow | null>(null);
  const [durations, setDurations] = useState<VersionDuration[] | null>(null);
  const [editing, setEditing] = useState(false);

  /*
   * 两路数据。拉不到就保持 null，展示态显「读取中……」——
   * 不编一个「没有」的结论（与卡页其余六路同一条纪律）。
   */
  const load = useCallback((): void => {
    void api.atcs().then(
      (data) => setRow(data.rows.find((r) => r.code === code) ?? null),
      () => undefined,
    );
    void api.execVersionDurations(code).then(
      (data) => setDurations(data.rows),
      () => undefined,
    );
  }, [code]);

  useEffect(() => {
    load();
  }, [load]);

  if (editing) {
    return (
      <div>
        {/*
          表单原样——字段、校验、「已有基线必填修订理由」的拦截都在它里面。
          保存后回展示态：重拉两路（基线值变了、均耗对照的分母也变了），并知会外层。
        */}
        <AtcBaselineForm
          code={code}
          onSaved={() => {
            setEditing(false);
            load();
            onChanged();
          }}
        />
        <div className="row-actions">
          <button type="button" className="btn quiet" onClick={() => setEditing(false)}>
            取消
          </button>
        </div>
      </div>
    );
  }

  if (row === null || durations === null) return <p className="sub-hint">读取中……</p>;

  return (
    <div>
      <BaselineComparison
        baselineDurationMin={row.baselineDurationMin}
        baselineFrequency={row.baselineFrequency}
        baselineFrequencyUnit={row.baselineFrequencyUnit}
        baselineFrequencyEstimated={row.baselineFrequencyEstimated}
        baselineDailyMinutes={row.baselineDailyMinutes}
        durations={durations}
        currentVersionNo={currentVersionNo}
      />
      {canManage && (
        <div className="row-actions">
          <button type="button" className="btn quiet" onClick={() => setEditing(true)}>
            ✎ 编辑
          </button>
        </div>
      )}
    </div>
  );
}
